// Fire-and-forget klientlogger för SYV-delning/nedladdning.
import { logSyvUsage } from "@/lib/syv-usage.functions";

export type SyvEventType =
  | "share_link"
  | "download_pdf"
  | "download_template"
  | "email_share"
  | "open_fullscreen"
  | "zero_results"
  | "next_action";

const SESSION_KEY = "syv:session_id";
const LAST_ZERO_KEY = "syv:last_zero"; // JSON: { sig, at, resource }

type LastZero = { sig: string; at: number; resource: string };

function randomId(): string {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  } catch { /* ignore */ }
  return `sv_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/** Stabilt sessions-id per flik (sessionStorage) — följer användaren genom hela besöket. */
export function getSyvSessionId(): string {
  if (typeof window === "undefined") return "";
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const id = randomId();
    window.sessionStorage.setItem(SESSION_KEY, id);
    return id;
  } catch {
    return "";
  }
}

function readLastZero(): LastZero | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(LAST_ZERO_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LastZero;
    if (!parsed || typeof parsed.sig !== "string") return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeLastZero(entry: LastZero | null): void {
  if (typeof window === "undefined") return;
  try {
    if (!entry) window.sessionStorage.removeItem(LAST_ZERO_KEY);
    else window.sessionStorage.setItem(LAST_ZERO_KEY, JSON.stringify(entry));
  } catch { /* ignore */ }
}

export function trackSyvEvent(
  event_type: SyvEventType,
  resource: string,
  extra?: { label?: string; meta?: Record<string, string | number | boolean | null> },
): void {
  if (typeof window === "undefined") return;
  try {
    const session_id = getSyvSessionId();
    const baseMeta: Record<string, string | number | boolean | null> = {
      ...(extra?.meta ?? {}),
      session_id,
    };

    if (event_type === "zero_results") {
      // Signaturen kommer typiskt från callern (educationType/county/municipality/…);
      // saknas den så härleds en enkel signatur ur meta.
      const providedSig = typeof extra?.meta?.filter_sig === "string" ? extra.meta.filter_sig : null;
      const sig = providedSig ?? JSON.stringify({
        e: extra?.meta?.educationType ?? null,
        c: extra?.meta?.countySlug ?? null,
        m: extra?.meta?.municipality ?? null,
        o: extra?.meta?.organizerType ?? null,
        v: extra?.meta?.valuesCount ?? null,
      });
      const zeroId = randomId();
      baseMeta.filter_sig = sig;
      baseMeta.zero_id = zeroId;
      writeLastZero({ sig, at: Date.now(), resource: resource.slice(0, 120) });
      // Spara även zero_id så efterföljande next_action kan bindas till just denna nollträff
      try { window.sessionStorage.setItem("syv:last_zero_id", zeroId); } catch { /* ignore */ }
    } else if (event_type === "next_action") {
      const last = readLastZero();
      if (last) {
        baseMeta.after_zero_results = true;
        baseMeta.zero_filter_sig = last.sig;
        baseMeta.zero_resource = last.resource;
        baseMeta.ms_since_zero = Math.max(0, Date.now() - last.at);
        try {
          const zid = window.sessionStorage.getItem("syv:last_zero_id");
          if (zid) baseMeta.zero_id = zid;
        } catch { /* ignore */ }
      } else {
        baseMeta.after_zero_results = false;
      }
    }

    void logSyvUsage({
      data: {
        event_type,
        resource: resource.slice(0, 120),
        label: extra?.label?.slice(0, 200) ?? null,
        meta: baseMeta,
      },
    }).catch(() => { /* tyst — spårning får aldrig störa UX */ });
  } catch { /* ignore */ }
}
